import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { useSuiClient, useSignAndExecuteTransaction } from "@mysten/dapp-kit";
import { toast } from "sonner";
import { api, shortAddr } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { lockTokens } from "@/lib/sui";
import CoinLogo from "@/components/CoinLogo";

function countdown(ms) {
  const d = ms - Date.now();
  if (d <= 0) return "unlocked";
  const days = Math.floor(d / 86400000);
  const hrs = Math.floor((d % 86400000) / 3600000);
  return days > 0 ? `${days}d ${hrs}h left` : `${hrs}h left`;
}

// Normalize a Sui coin type so a trimmed RPC form matches our stored full form.
const normType = (t) => {
  if (!t) return "";
  const [addr, ...rest] = t.split("::");
  return addr.replace(/^0x/, "").replace(/^0+/, "") + "::" + rest.join("::");
};

function NewVesting({ vestPkg, onLocked }) {
  const { address, connected } = useWallet();
  const client = useSuiClient();
  const { mutateAsync: signExec } = useSignAndExecuteTransaction();
  const [held, setHeld] = useState([]);
  const [sel, setSel] = useState("");
  const [amt, setAmt] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadHeldInflightRef = useRef(false);
  const loadHeld = useCallback(async () => {
    if (!address || loadHeldInflightRef.current) { if (!address) setHeld([]); return; }
    loadHeldInflightRef.current = true;
    setLoading(true);
    try {
      const [{ data: coins }, balances] = await Promise.all([
        api.get("/coins/onchain"),
        client.listBalances({ owner: address, limit: 500 }).then((r) => r.balances),
      ]);
      const balMap = {};
      for (const b of balances) balMap[normType(b.coinType)] = BigInt(b.balance);
      const list = [];
      for (const c of coins) {
        const base = balMap[normType(c.coin_type)] || 0n;
        if (base > 0n) {
          const dec = c.decimals || 9;
          list.push({ ...c, base, balance: Number(base) / 10 ** dec });
        }
      }
      setHeld(list);
      if (list.length && !list.find((x) => x.coin_type === sel)) setSel(list[0].coin_type);
    } catch (e) { setHeld([]); }
    finally { setLoading(false); }
  }, [address, client]); // eslint-disable-line

  useEffect(() => { if (connected) loadHeld(); else setHeld([]); }, [connected, loadHeld]);

  const coin = held.find((c) => c.coin_type === sel);
  const isDev = coin && address && coin.creator && address.toLowerCase() === coin.creator.toLowerCase();

  const doLock = async () => {
    if (!coin) { toast.error("Pick a token"); return; }
    const a = parseFloat(amt);
    if (!a || a <= 0) { toast.error("Enter an amount"); return; }
    if (a > coin.balance) { toast.error(`You only have ${fmtNum(coin.balance)} ${coin.symbol}`); return; }
    setBusy(true);
    try {
      const base = BigInt(Math.floor(a * 10 ** (coin.decimals || 9)));
      await lockTokens(signExec, client, { vestingPkg: vestPkg, coinType: coin.coin_type, amountBase: base, days, isDev }, address);
      toast.success(`Locked ${a} ${coin.symbol} for ${days} days`);
      setAmt("");
      setTimeout(() => { loadHeld(); onLocked && onLocked(); }, 2500);
    } catch (e) { toast.error(e?.message?.slice(0, 80) || "Lock failed"); }
    finally { setBusy(false); }
  };

  if (!connected) {
    return (
      <div className="card-surface p-5 mb-5 text-sm text-muted-foreground" data-testid="new-vesting-connect">
        Connect your wallet to lock tokens in the vesting contract.
      </div>
    );
  }

  return (
    <div className="card-surface p-5 mb-5" data-testid="new-vesting">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-3">New vesting lock</div>
      {loading ? (
        <div className="text-sm text-muted-foreground py-2">Loading your tokens…</div>
      ) : held.length === 0 ? (
        <div className="text-sm text-muted-foreground py-2" data-testid="new-vesting-empty">You don't hold any launchpad tokens to lock yet.</div>
      ) : (
        <>
          <select value={sel} onChange={(e) => { setSel(e.target.value); setAmt(""); }} data-testid="vesting-coin-select"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono mb-2 focus:outline-none focus:border-[#1EC62E]/50">
            {held.map((c) => (
              <option key={c.coin_type} value={c.coin_type} className="bg-[#0a0a0a]">{c.symbol} — {fmtNum(c.balance)}</option>
            ))}
          </select>
          {coin && (
            <div className="flex items-center justify-between mb-1.5 text-[11px] font-mono" data-testid="new-vesting-balance">
              <span className="text-muted-foreground">Available: <span className="text-white font-bold">{fmtNum(coin.balance)}</span> {coin.symbol}{isDev ? " (dev)" : ""}</span>
              <button type="button" onClick={() => setAmt(String(coin.balance))} data-testid="new-vesting-max" className="px-2 py-0.5 rounded bg-[#1EC62E]/15 text-[#1EC62E] font-bold border border-[#1EC62E]/40">Max</button>
            </div>
          )}
          <input value={amt} onChange={(e) => setAmt(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Amount to lock" data-testid="new-vesting-amount"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono mb-2 focus:outline-none focus:border-[#1EC62E]/50" />
          <div className="flex items-center gap-2 mb-3">
            {[7, 30, 60].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`new-vesting-days-${d}`}
                className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${days === d ? "border-[#1EC62E]/60 text-[#1EC62E] bg-[#1EC62E]/10" : "border-white/10 text-muted-foreground"}`}>{d} days</button>
            ))}
          </div>
          <button onClick={doLock} disabled={busy || !vestPkg} data-testid="new-vesting-submit"
            className="w-full py-2.5 rounded-lg font-bold text-black disabled:opacity-40" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>
            {busy ? "Locking…" : `Lock for ${days} days`}
          </button>
          <p className="text-[10px] text-muted-foreground mt-2">Tokens are held safely in the vesting contract and can only return to your wallet after the lock ends. No admin can touch them.</p>
        </>
      )}
    </div>
  );
}

export default function Vesting() {
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vestPkg, setVestPkg] = useState(null);

  const load = useCallback(() => {
    api.get("/vesting/all").then(({ data }) => setLocks(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/vesting/config").then(({ data }) => setVestPkg(data.vesting_package_id)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6" data-testid="vesting-page">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-5 h-5 text-[#FFB800]" />
        <h1 className="font-display font-extrabold text-2xl">Token Vesting</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Tokens locked on-chain in the vesting contract — held safely and returnable only to the locker after the period ends. No admin can touch them.</p>

      <NewVesting vestPkg={vestPkg} onLocked={load} />

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Loading…</div>
      ) : locks.length === 0 ? (
        <div className="card-surface p-10 text-center text-muted-foreground text-sm">No active locks yet.</div>
      ) : (
        <div className="space-y-2" data-testid="vesting-all-list">
          {locks.map((l) => (
            <Link to={`/coin/${l.curve_id}`} key={l.lock_id} className="card-surface p-4 flex items-center gap-3 hover:border-[#FFB800]/40 transition-colors">
              <CoinLogo coin={l} size={40} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-bold text-sm">{l.symbol || "—"}</span>
                  {l.is_dev && <span className="px-1.5 py-0.5 rounded bg-[#FFB800]/20 text-[#FFB800] text-[9px] font-bold">DEV</span>}
                </div>
                <div className="text-xs text-muted-foreground font-mono">{shortAddr(l.owner)} · {l.days}d lock</div>
              </div>
              <div className="text-right font-mono">
                <div className="font-bold text-sm">{fmtNum(l.amount)} <span className="text-muted-foreground text-xs">{l.symbol}</span></div>
                <div className={`text-xs ${l.claimable ? "text-[#1EC62E]" : "text-muted-foreground"}`}>{countdown(l.unlock_ms)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
